/**
 * Vanilla entity-model geometry (ModelPart / ModelPart.Cube / PoseStack),
 * ported directly so block-entity models — the chest and the bed — come out of
 * the same maths Minecraft uses, with no hand-placed texture patches.
 *
 * A model is a set of parts; a part is cuboids plus a pose (offset + ZYX
 * rotation), all in 1/16-block pixels. Each cuboid's six faces sample the
 * model's 64x64 sheet with the classic box unwrap at its `texOffs`. Each
 * distinct face rect gets its own atlas tile (allocated by blocks.ts, painted
 * by textures.ts at the normal 2px-per-texel density from the top-left), and
 * `bakeQuads` turns a model + renderer transform into block-space quads whose
 * UVs index into those tiles.
 *
 * Pure data + maths (no DOM, no block imports) so blocks.ts, the mesh worker
 * and the HUD can all import it.
 */

// --- 3x4 affine matrices (row-major), PoseStack semantics ---------------------

export type Mat = readonly number[]; // [r00 r01 r02 tx, r10 r11 r12 ty, r20 r21 r22 tz]

export const IDENTITY: Mat = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0];

/** a * b (apply b first). */
export function mul(a: Mat, b: Mat): Mat {
  const o: number[] = new Array(12);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 4; c++) {
      let s = c === 3 ? a[r * 4 + 3] : 0;
      for (let k = 0; k < 3; k++) s += a[r * 4 + k] * b[k * 4 + c];
      o[r * 4 + c] = s;
    }
  }
  return o;
}
export const translate = (x: number, y: number, z: number): Mat => [1, 0, 0, x, 0, 1, 0, y, 0, 0, 1, z];
export function rotX(a: number): Mat {
  const c = Math.cos(a), s = Math.sin(a);
  return [1, 0, 0, 0, 0, c, -s, 0, 0, s, c, 0];
}
export function rotY(a: number): Mat {
  const c = Math.cos(a), s = Math.sin(a);
  return [c, 0, s, 0, 0, 1, 0, 0, -s, 0, c, 0];
}
export function rotZ(a: number): Mat {
  const c = Math.cos(a), s = Math.sin(a);
  return [c, -s, 0, 0, s, c, 0, 0, 0, 0, 1, 0];
}
/** Chain of PoseStack operations, applied in call order (each post-multiplies). */
export const chain = (...ms: Mat[]): Mat => ms.reduce(mul, IDENTITY);

function apply(m: Mat, x: number, y: number, z: number, w = 1): [number, number, number] {
  return [
    m[0] * x + m[1] * y + m[2] * z + m[3] * w,
    m[4] * x + m[5] * y + m[6] * z + m[7] * w,
    m[8] * x + m[9] * y + m[10] * z + m[11] * w,
  ];
}

// --- model definitions ---------------------------------------------------------

export interface Cube {
  /** CubeListBuilder.texOffs */
  tex: readonly [number, number];
  /** addBox origin and size, pixels */
  from: readonly [number, number, number];
  size: readonly [number, number, number];
}
export interface Part {
  cubes: readonly Cube[];
  /** PartPose offset (pixels) and xRot/yRot/zRot (radians). */
  offset?: readonly [number, number, number];
  rot?: readonly [number, number, number];
  /** This part samples another sheet than the model's (the trapped chest's latch on the plain chest's body). */
  sheet?: string;
}
export interface EntityModel {
  /** Sheet path under public/mc, without .png. */
  sheet: string;
  parts: Record<string, Part>;
}

const PI = Math.PI;

/** ChestRenderer.createSingleBodyLayer. */
export const CHEST_MODEL: EntityModel = {
  sheet: 'chest_entity',
  parts: {
    bottom: { cubes: [{ tex: [0, 19], from: [1, 0, 1], size: [14, 10, 14] }] },
    lid: { cubes: [{ tex: [0, 0], from: [1, 0, 0], size: [14, 5, 14] }], offset: [0, 9, 1] },
    lock: { cubes: [{ tex: [0, 0], from: [7, -2, 14], size: [2, 4, 1] }], offset: [0, 9, 1] },
  },
};

/** BedRenderer.createHeadLayer. */
export const BED_HEAD_MODEL: EntityModel = {
  sheet: 'entity/bed/red',
  parts: {
    main: { cubes: [{ tex: [0, 0], from: [0, 0, 0], size: [16, 16, 6] }] },
    left_leg: { cubes: [{ tex: [50, 6], from: [0, 6, 0], size: [3, 3, 3] }], rot: [PI / 2, 0, PI / 2] },
    right_leg: { cubes: [{ tex: [50, 18], from: [-16, 6, 0], size: [3, 3, 3] }], rot: [PI / 2, 0, PI] },
  },
};

/** BedRenderer.createFootLayer. */
export const BED_FOOT_MODEL: EntityModel = {
  sheet: 'entity/bed/red',
  parts: {
    main: { cubes: [{ tex: [0, 22], from: [0, 0, 0], size: [16, 16, 6] }] },
    left_leg: { cubes: [{ tex: [50, 0], from: [0, 6, -16], size: [3, 3, 3] }], rot: [PI / 2, 0, 0] },
    right_leg: { cubes: [{ tex: [50, 12], from: [-16, 6, -16], size: [3, 3, 3] }], rot: [PI / 2, 0, 3 * PI / 2] },
  },
};

/** ChestRenderer.createDoubleBodyLeftLayer / RightLayer: the half that reaches its cell's -X / +X edge (before the facing turn). */
const chestHalf = (sheet: string, x0: number, lockX: number, lockSheet?: string): EntityModel => ({
  sheet,
  parts: {
    bottom: { cubes: [{ tex: [0, 19], from: [x0, 0, 1], size: [15, 10, 14] }] },
    lid: { cubes: [{ tex: [0, 0], from: [x0, 0, 0], size: [15, 5, 14] }], offset: [0, 9, 1] },
    lock: { cubes: [{ tex: [0, 0], from: [lockX, -2, 14], size: [1, 4, 1] }], offset: [0, 9, 1], sheet: lockSheet },
  },
});
const withLock = (m: EntityModel, sheet: string): EntityModel => ({ sheet: m.sheet, parts: { ...m.parts, lock: { ...m.parts.lock, sheet } } });
export const CHEST_LEFT_MODEL = chestHalf('entity/chest/normal_left', 0, 0);
export const CHEST_RIGHT_MODEL = chestHalf('entity/chest/normal_right', 1, 15);
export const ENDER_CHEST_MODEL: EntityModel = { sheet: 'entity/chest/ender', parts: CHEST_MODEL.parts };
/** Trapped chests are the plain chest with a red latch: only the latch samples the trapped sheets. */
export const TRAPPED_CHEST_MODEL = withLock(CHEST_MODEL, 'entity/chest/trapped');
export const TRAPPED_LEFT_MODEL = withLock(CHEST_LEFT_MODEL, 'entity/chest/trapped_left');
export const TRAPPED_RIGHT_MODEL = withLock(CHEST_RIGHT_MODEL, 'entity/chest/trapped_right');
/** [plain, trapped] x [single, left, right] (game/utility/defs.ts chest kind and type). */
export const CHEST_MODELS: ReadonlyArray<ReadonlyArray<EntityModel>> = [
  [CHEST_MODEL, CHEST_LEFT_MODEL, CHEST_RIGHT_MODEL], [TRAPPED_CHEST_MODEL, TRAPPED_LEFT_MODEL, TRAPPED_RIGHT_MODEL],
];

// New models go at the end: FACE_TILES order is the atlas order.
/** SignRenderer.createSignLayer (64x32 sheet), one per wood: the board, and the standing sign's stick. */
export const SIGN_SHEET_WOODS = ['oak', 'spruce', 'birch', 'jungle', 'acacia', 'dark_oak', 'mangrove', 'cherry', 'pale_oak'] as const;
const signModel = (wood: string): EntityModel => ({
  sheet: 'entity/signs/' + wood,
  parts: {
    sign: { cubes: [{ tex: [0, 0], from: [-12, -14, -1], size: [24, 12, 2] }] },
    stick: { cubes: [{ tex: [0, 14], from: [-1, -2, -1], size: [2, 14, 2] }] },
  },
});
export const SIGN_MODELS: readonly EntityModel[] = SIGN_SHEET_WOODS.map(signModel);
/** ShulkerModel (64x64 sheet): lid and base, both posed at y 24. */
export const SHULKER_BOX_MODEL: EntityModel = {
  sheet: 'entity/shulker/shulker',
  parts: {
    lid: { cubes: [{ tex: [0, 0], from: [-8, -16, -8], size: [16, 12, 16] }], offset: [0, 24, 0] },
    base: { cubes: [{ tex: [0, 28], from: [-8, -8, -8], size: [16, 8, 16] }], offset: [0, 24, 0] },
  },
};

export const ENTITY_MODELS: readonly EntityModel[] = [
  CHEST_MODEL, BED_HEAD_MODEL, BED_FOOT_MODEL,
  CHEST_LEFT_MODEL, CHEST_RIGHT_MODEL, ENDER_CHEST_MODEL, TRAPPED_CHEST_MODEL, TRAPPED_LEFT_MODEL, TRAPPED_RIGHT_MODEL,
  ...SIGN_MODELS, SHULKER_BOX_MODEL,
];

// --- the box unwrap (ModelPart.Cube) --------------------------------------------

interface RawFace {
  /** model-space corners, vanilla's polygon order */
  pts: Array<[number, number, number]>;
  /** sheet texel coords per corner */
  uvs: Array<[number, number]>;
  /** model-space outward normal */
  n: [number, number, number];
}

/** The six polygons of ModelPart.Cube, vertices and UVs exactly as vanilla. */
function cubeFaces(c: Cube): RawFace[] {
  const [x0, y0, z0] = c.from;
  const [w, h, d] = c.size;
  const x1 = x0 + w, y1 = y0 + h, z1 = z0 + d;
  const v7: [number, number, number] = [x0, y0, z0];
  const v0: [number, number, number] = [x1, y0, z0];
  const v1: [number, number, number] = [x1, y1, z0];
  const v2: [number, number, number] = [x0, y1, z0];
  const v3: [number, number, number] = [x0, y0, z1];
  const v4: [number, number, number] = [x1, y0, z1];
  const v5: [number, number, number] = [x1, y1, z1];
  const v6: [number, number, number] = [x0, y1, z1];
  const [u, v] = c.tex;
  const f4 = u, f5 = u + d, f6 = u + d + w, f7 = u + d + w + w, f8 = u + d + w + d, f9 = u + d + w + d + w;
  const f10 = v, f11 = v + d, f12 = v + d + h;
  // Polygon(vertices, u1, v1, u2, v2): [0]=(u2,v1) [1]=(u1,v1) [2]=(u1,v2) [3]=(u2,v2)
  const poly = (
    pts: Array<[number, number, number]>, u1: number, vv1: number, u2: number, vv2: number,
    n: [number, number, number],
  ): RawFace => ({ pts, uvs: [[u2, vv1], [u1, vv1], [u1, vv2], [u2, vv2]], n });
  return [
    poly([v4, v3, v7, v0], f5, f10, f6, f11, [0, -1, 0]),
    poly([v1, v2, v6, v5], f6, f11, f7, f10, [0, 1, 0]),
    poly([v7, v3, v6, v2], f4, f11, f5, f12, [-1, 0, 0]),
    poly([v0, v7, v2, v1], f5, f11, f6, f12, [0, 0, -1]),
    poly([v4, v0, v1, v5], f6, f11, f8, f12, [1, 0, 0]),
    poly([v3, v4, v5, v6], f8, f11, f9, f12, [0, 0, 1]),
  ];
}

/** cubeFaces, with a face wider than one tile (the sign's 24-texel board) cut into two halves along u. */
function tileFaces(c: Cube): RawFace[] {
  const out: RawFace[] = [];
  for (const f of cubeFaces(c)) {
    const [a, b, cc, d] = f.uvs;
    if (Math.abs(a[0] - b[0]) <= FACE_TILE_TEXELS) { out.push(f); continue; }
    const mid = (p: readonly number[], q: readonly number[]): [number, number, number] => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2, (p[2] + q[2]) / 2];
    const m01 = mid(f.pts[0], f.pts[1]), m32 = mid(f.pts[3], f.pts[2]);
    const um = (a[0] + b[0]) / 2;
    out.push({ pts: [f.pts[0], m01, m32, f.pts[3]], uvs: [a, [um, a[1]], [um, d[1]], d], n: f.n });
    out.push({ pts: [m01, f.pts[1], f.pts[2], m32], uvs: [[um, b[1]], b, cc, [um, cc[1]]], n: f.n });
  }
  return out;
}

// --- face tiles -------------------------------------------------------------------

/** One atlas tile: a rect of a sheet (texels), painted at the tile's top-left. */
export interface FaceTile {
  sheet: string;
  rect: readonly [number, number, number, number]; // x, y, w, h
}

/** Texels per tile side (tile px / 2). Every rect must fit. */
export const FACE_TILE_TEXELS = 16;

/** The sheet rect a cube's whole unwrap covers. */
function cubeRect(c: Cube): [number, number, number, number] {
  const [w, h, d] = c.size;
  return [c.tex[0], c.tex[1], 2 * (d + w), d + h];
}
function faceRect(f: RawFace): [number, number, number, number] {
  const us = f.uvs.map((p) => p[0]), vs = f.uvs.map((p) => p[1]);
  const x = Math.min(...us), y = Math.min(...vs);
  return [x, y, Math.max(...us) - x, Math.max(...vs) - y];
}
const fits = (r: readonly number[]): boolean => r[2] <= FACE_TILE_TEXELS && r[3] <= FACE_TILE_TEXELS;
const rectKey = (sheet: string, r: readonly number[]): string => `${sheet}:${r.join(',')}`;

/** The tile rect a face samples from: its whole cube's unwrap when that fits
 *  one tile (legs, the latch — one tile instead of six), else its own rect. */
function tileRectOf(c: Cube, f: RawFace): [number, number, number, number] {
  const cr = cubeRect(c);
  return fits(cr) ? cr : faceRect(f);
}

/** Every distinct tile rect of every model, in a fixed order; blocks.ts gives
 *  entry i the tile id ENTITY_TILE_BASE + i. */
export const FACE_TILES: readonly FaceTile[] = (() => {
  const out: FaceTile[] = [];
  const seen = new Set<string>();
  for (const m of ENTITY_MODELS) {
    for (const p of Object.values(m.parts)) {
      for (const c of p.cubes) {
        for (const f of tileFaces(c)) {
          const rect = tileRectOf(c, f);
          const sheet = p.sheet ?? m.sheet;
          const k = rectKey(sheet, rect);
          if (seen.has(k)) continue;
          if (!fits(rect)) throw new Error(`entity face ${k} exceeds one tile`);
          seen.add(k);
          out.push({ sheet, rect });
        }
      }
    }
  }
  return out;
})();
const FACE_INDEX = new Map(FACE_TILES.map((f, i) => [rectKey(f.sheet, f.rect), i]));

// --- baking ----------------------------------------------------------------------

/** A finished quad in block space (blocks, not pixels), wound counter-clockwise
 *  seen from outside, with tile-local UVs. */
export interface Quad {
  pos: readonly number[]; // 4 x (x, y, z)
  uv: readonly number[];  // 4 x (u, v)
  tile: number;
  /** Axis face it points along (0..5 = +X,-X,+Y,-Y,+Z,-Z), or 6 if oblique. */
  face: number;
  /** Vertex colour (0..255 each) multiplied into the texture: redstone dust by power. */
  tint?: readonly [number, number, number];
}

/** Map an outward normal to the mesher's face id, or 6 when not axis-aligned. */
export function faceOfNormal(n: readonly number[]): number {
  for (let a = 0; a < 3; a++) {
    if (n[a] > 0.99) return a * 2;
    if (n[a] < -0.99) return a * 2 + 1;
  }
  return 6;
}

/**
 * Bake `model` under the renderer transform `root` (block units in, block units
 * out — the PoseStack before model.renderToBuffer). `partRot` overrides a
 * part's xRot (the chest lid's opening angle). `tileBase` is the first entity
 * tile id. Mirrors ModelPart.render: translate(offset/16), rotateZYX, then each
 * vertex at pos/16.
 */
export function bakeQuads(
  model: EntityModel, root: Mat, tileBase: number, partXRot: Record<string, number> = {},
): Quad[] {
  const out: Quad[] = [];
  for (const [name, p] of Object.entries(model.parts)) {
    const o = p.offset ?? [0, 0, 0];
    const r = p.rot ?? [0, 0, 0];
    const xr = partXRot[name] ?? r[0];
    const m = chain(
      root, translate(o[0] / 16, o[1] / 16, o[2] / 16),
      rotZ(r[2]), rotY(r[1]), rotX(xr),
      [1 / 16, 0, 0, 0, 0, 1 / 16, 0, 0, 0, 0, 1 / 16, 0],
    );
    for (const c of p.cubes) {
      for (const f of tileFaces(c)) {
        const rect = tileRectOf(c, f);
        const tile = tileBase + FACE_INDEX.get(rectKey(p.sheet ?? model.sheet, rect))!;
        const pts = f.pts.map((q) => apply(m, q[0], q[1], q[2]));
        const n = apply(m, f.n[0], f.n[1], f.n[2], 0);
        const uvs = f.uvs.map(([u, v]): [number, number] =>
          [(u - rect[0]) / FACE_TILE_TEXELS, (v - rect[1]) / FACE_TILE_TEXELS]);
        out.push(orientedQuad(pts, uvs, tile, n));
      }
    }
  }
  return out;
}

/** A quad from four corners in order around it, wound counter-clockwise seen
 *  from the side `n` points to, with its face id read off `n`. */
export function orientedQuad(
  pts: ReadonlyArray<readonly number[]>, uvs: ReadonlyArray<readonly number[]>, tile: number, n: readonly number[],
): Quad {
  const len = Math.hypot(n[0], n[1], n[2]);
  const nn = [n[0] / len, n[1] / len, n[2] / len];
  const ax = pts[1][0] - pts[0][0], ay = pts[1][1] - pts[0][1], az = pts[1][2] - pts[0][2];
  const bx = pts[2][0] - pts[1][0], by = pts[2][1] - pts[1][1], bz = pts[2][2] - pts[1][2];
  const cross = (ay * bz - az * by) * nn[0] + (az * bx - ax * bz) * nn[1] + (ax * by - ay * bx) * nn[2];
  const order = cross < 0 ? [0, 3, 2, 1] : [0, 1, 2, 3];
  return {
    pos: order.flatMap((k) => [pts[k][0], pts[k][1], pts[k][2]]),
    uv: order.flatMap((k) => [uvs[k][0], uvs[k][1]]),
    tile,
    face: faceOfNormal(nn),
  };
}

// --- the two block-entity renderers -------------------------------------------

/** Direction.toYRot for the cell-space facings used by chest ids
 *  (0 = +Z/south, 1 = +X/east, 2 = -Z/north, 3 = -X/west). */
const FACING_YROT = [0, 270, 180, 90];
const deg = (d: number): number => (d * PI) / 180;

const scale = (x: number, y: number, z: number): Mat => [x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0];

/** SignRenderer: a standing sign turned `angle` (radians, front from +Z towards +X), or a wall sign hung on the block behind it. */
export function signQuads(wood: number, angle: number, wall: boolean, tileBase: number): Quad[] {
  const m = SIGN_MODELS[wood] ?? SIGN_MODELS[0];
  const s = 2 / 3;
  const root = wall
    ? chain(translate(0.5, 0.5, 0.5), rotY(angle), translate(0, -0.3125, -0.4375), scale(s, -s, -s))
    : chain(translate(0.5, 0.5, 0.5), rotY(angle), scale(s, -s, -s));
  return bakeQuads(wall ? { sheet: m.sheet, parts: { sign: m.parts.sign } } : m, root, tileBase);
}

/** ShulkerBoxRenderer, closed and facing up. */
export function shulkerBoxQuads(tileBase: number): Quad[] {
  return bakeQuads(SHULKER_BOX_MODEL, chain(translate(0.5, 0.5, 0.5), scale(1, -1, -1), translate(0, -1, 0)), tileBase);
}

/** ChestRenderer.render: yaw about the block centre, lid + lock xRot = -openness*90°. */
export function chestQuads(facing: number, open: boolean, tileBase: number, model: EntityModel = CHEST_MODEL): Quad[] {
  const root = chain(translate(0.5, 0.5, 0.5), rotY(deg(-FACING_YROT[facing])), translate(-0.5, -0.5, -0.5));
  const lidRot = open ? -PI / 2 : 0;
  return bakeQuads(model, root, tileBase, { lid: lidRot, lock: lidRot });
}

/** BedRenderer.renderPiece (world placement, foot = false). `facing` is the
 *  direction from the foot half to the head half, in the chest facing order. */
export function bedQuads(head: boolean, facing: number, tileBase: number): Quad[] {
  const root = chain(
    translate(0, 0.5625, 0), rotX(PI / 2),
    translate(0.5, 0.5, 0.5), rotZ(PI + deg(FACING_YROT[facing])), translate(-0.5, -0.5, -0.5),
  );
  return bakeQuads(head ? BED_HEAD_MODEL : BED_FOOT_MODEL, root, tileBase);
}
